import { ChevronLeftOutlined, ChevronRightOutlined } from '@mui/icons-material';
import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { JSX, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Location, NavigateFunction, useLocation, useNavigate } from 'react-router-dom';
import {
    addDays,
    formatDayLabel,
    startOfWeek,
    toDateInputValue,
    toDateTimeInputValue,
} from '../../../library/dateTimeHelper';
import { CalendarLocationState } from '../../../library/constants';
import { IAppointment, OCCUPYING_STATUSES } from '../../../library/models/appointment';
import { memberFullName } from '../../../library/membershipDisplay';
import { splitApiError } from '../../../library/useApiFormError';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useHorizontalOverflow } from '../../../library/useHorizontalOverflow';
import { useMirroredScrollLeft } from '../../../library/useMirroredScrollLeft';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useAuth } from '../../../contexts/AuthContext';
import { CreateAppointmentRequest } from '../../../library/models/appointment';
import { useSnackbar } from '../../layout/snackbarProvider';
import { PendingRequests } from './PendingRequests';
import { AppointmentDetailDialog } from '../../appointmentDetail/AppointmentDetailDialog';
import { DayHeaderRow } from './DayHeaderRow';
import { AddPatientDialog } from '../../addPatientDialog/AddPatientDialog';
import { NewEntryDialog } from './NewEntryDialog';
import { ScheduleGrid } from './ScheduleGrid';
import { AddPatientFormValues, IPatient, toInviteCustomerRequest } from '../../../library/models/clinic';
import { ITherapistProgram } from '../../../library/models/therapistProgram';
import { currentTenantId } from '../../../library/brandingCache';
import { groupByDay, hourBoundsFor } from './scheduleEntries';
import {
    HOUR_HEIGHT,
    LABEL_COLUMN,
    WEEK_COLUMN_MIN,
    dayControlsStyles,
    dayHeaderRailStyles,
    dayLabelStyles,
    dayPagerArrowStyles,
    dayPagerStyles,
    emptyStateStyles,
    headerBoxStyles,
    loadingBoxStyles,
    scrollHintStyles,
    scrollTrackStyles,
    scrollViewportStyles,
    showCancelledToggleStyles,
    titleStyles,
    viewToggleStyles,
    weekFrameStyles,
} from './myCalendar.styles';

/**
 * How often the now-line catches up with the clock.
 *
 * An hour is 72px, so a minute of drift is 1.2px -- nothing a therapist could see. Ticking
 * faster would only re-render the page for no visible gain.
 */
const TICK_MS = 60_000;

const DAYS_IN_WEEK = 7;

type ScheduleView = 'day' | 'week';

/** Where the therapist's last choice of view is kept between visits. */
const VIEW_STORAGE_KEY = 'scheduleView';

/**
 * The view to open on.
 *
 * Day, unless the therapist has said otherwise before. Wrapped because localStorage throws
 * outright in a locked-down browser, and a page that will not render is a worse answer to
 * that than forgetting a preference.
 */
const storedView = (): ScheduleView => {
    try {
        return window.localStorage.getItem(VIEW_STORAGE_KEY) === 'week' ? 'week' : 'day';
    } catch {
        return 'day';
    }
};

const rememberView = (view: ScheduleView): void => {
    try {
        window.localStorage.setItem(VIEW_STORAGE_KEY, view);
    } catch {
        // A therapist who cannot store a preference still gets the view they clicked.
    }
};

/** Whether the therapist asked to see what was called off as well. */
const SHOW_CANCELLED_STORAGE_KEY = 'calendarShowCancelled';

const storedShowCancelled = (): boolean => {
    try {
        return window.localStorage.getItem(SHOW_CANCELLED_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

const rememberShowCancelled = (showCancelled: boolean): void => {
    try {
        window.localStorage.setItem(SHOW_CANCELLED_STORAGE_KEY, String(showCancelled));
    } catch {
        // Same bargain as the view: the click still works, it is just not remembered.
    }
};

/** The seven dates of the week an ISO date falls in, Monday first. */
const weekOf = (isoDate: string): string[] => {
    const monday = startOfWeek(isoDate);

    return Array.from({ length: DAYS_IN_WEEK }, (_, index) => addDays(monday, index));
};

/**
 * A therapist's own diary, a day or a week at a time.
 *
 * There was no such page before: appointments were Google Calendar events, so Google's own
 * UI was the diary. This is the replacement, and it shows the therapist's own work and
 * never a colleague's -- which the API enforces by pinning the query to the caller rather
 * than trusting a parameter.
 *
 * Everything that occupies time is here, blocks of leave included. That is the whole point
 * of keeping them in the same table: a view that showed only patient appointments would
 * show a free hour the therapist is not actually free in.
 *
 * The week exists because the day was too narrow to answer the questions therapists
 * actually have -- where the gaps are, which days are heavy, and which finished sessions
 * nobody has marked yet. That last one was invisible before at any distance: an unanswered
 * session only ever flagged itself on the day it happened.
 */
export const MyCalendar = (): JSX.Element => {
    const { t: translate, i18n } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { user } = useAuth();

    /*
     * Two things watch the week's sideways scroller: the hint below it, which only appears
     * while there is something to scroll to, and the sticky day-name rail above it, which
     * has to slide with it. Both hand back a callback ref, and one element can only take
     * one -- hence the composed ref below. Every piece of it is stable, so React attaches
     * and detaches once rather than on every render.
     */
    const [overflowRef, isWeekScrollable] = useHorizontalOverflow<HTMLDivElement>();
    const [mirroredScrollerRef, dayHeaderRailRef] = useMirroredScrollLeft<
        HTMLDivElement,
        HTMLDivElement
    >();

    const weekViewportRef = useCallback(
        (node: HTMLDivElement | null): void => {
            overflowRef(node);
            mirroredScrollerRef(node);
        },
        [overflowRef, mirroredScrollerRef]
    );

    const [view, setView] = useState<ScheduleView>(storedView);

    /**
     * Whether what was called off is on the grid too.
     *
     * Off by default, because a calendar answers "what am I doing" and a cancelled hour is
     * free time. It is a toggle rather than a rule, because reopening a mis-cancelled
     * booking is only possible from its own card — and because "what happened to that
     * Tuesday" is a real question, even if My Appointments is the better place to ask it.
     */
    const [showCancelled, setShowCancelled] = useState<boolean>(storedShowCancelled);

    /**
     * The date the view is built around: the day on show, or a day in the week on show.
     *
     * One piece of state for both, so switching view keeps the therapist where they were
     * rather than throwing them back to today.
     */
    const [anchor, setAnchor] = useState<string>(toDateInputValue(new Date()));

    const [selected, setSelected] = useState<IAppointment | null>(null);

    /**
     * My Appointments sends a therapist here to create an appointment, since booking
     * proper is the customer's page and gated to them. The dialog is open from the first
     * render rather than opened by the effect below, so the press produces the dialog and
     * not a flash of the diary behind it.
     */
    const location: Location = useLocation();
    const navigate: NavigateFunction = useNavigate();
    const openNewEntryRequested: boolean =
        (location.state as CalendarLocationState | null)?.openNewEntry === true;

    const [isAdding, setIsAdding] = useState<boolean>(openNewEntryRequested);

    /**
     * The empty half hour the therapist clicked, if that is how the dialog was opened.
     *
     * Null for every other way in -- the header button, and My Appointments sending them
     * here -- which is what keeps those on the seeded hour and the free-hour list they have
     * always had. It is cleared on both of them for the same reason `createdPatientId` is:
     * a resolved value left lying about would have the next entry open on the hour clicked
     * last time, behind the therapist's back.
     */
    const [slotStart, setSlotStart] = useState<string | null>(null);

    // The add-patient dialog that stacks over the one above. Siblings rather than nested,
    // which is how every other stacked pair here is built.
    const [isAddPatientOpen, setIsAddPatientOpen] = useState<boolean>(false);
    const [createdPatientId, setCreatedPatientId] = useState<number | null>(null);

    // Consumed once: the history entry is replaced without it, so reloading this page or
    // coming Back to it shows the diary rather than reopening the dialog.
    useEffect(() => {
        if (!openNewEntryRequested) {
            return;
        }

        setIsAdding(true);
        navigate(location.pathname, { replace: true, state: null });
    }, [openNewEntryRequested, location.pathname, navigate]);

    /**
     * The wall clock, kept current so the now-line moves on its own.
     *
     * Held in state rather than read at render time so that a page left open all morning
     * still tells the truth -- including across midnight, which is why `today` below is
     * derived from this and not from a fresh `new Date()`.
     *
     * A real Date is right here, even though every other time on this page is parsed as a
     * string: API times are the clinic's wall clock and the therapist is at the clinic, so
     * the browser's clock is the same clock.
     */
    const [now, setNow] = useState<Date>(new Date());

    useEffect(() => {
        const tick = setInterval(() => setNow(new Date()), TICK_MS);

        return () => clearInterval(tick);
    }, []);

    const isWeek: boolean = view === 'week';
    const days: string[] = isWeek ? weekOf(anchor) : [anchor];
    const from: string = days[0];
    const to: string = days[days.length - 1];

    /*
     * The range is inclusive at both ends, and the API pins the therapist to the caller,
     * so a week costs exactly one request more than a day did.
     *
     * Only what occupies time, unless the therapist asked otherwise. A cancelled booking
     * frees its slot the instant it is called off, so the replacement is a real
     * appointment on the same hour -- and the grid draws its cards in one absolutely
     * positioned lane, which put the two on top of each other. Asking for the two
     * occupying statuses (a pending request among them) makes that impossible rather than handled: the API's own overlap
     * guard refuses two of them clashing for one therapist. Turning the toggle on can
     * therefore stack cards, and that is the honest cost of drawing history on a grid.
     *
     * The params are part of the React Query key, so toggling refetches on its own.
     */
    const { data, isLoading, refetch } = useReactQuery<IAppointment[]>(
        services.appointments.getAppointments(
            from,
            to,
            undefined,
            undefined,
            showCancelled ? undefined : OCCUPYING_STATUSES
        )
    );

    /**
     * This therapist's own working hours, so the grid draws the day *they* actually work
     * rather than the eight-to-seven the constants used to assume -- or, for somebody who
     * has set none, their clinic's, which is what the API answers with. Read here and
     * written on My Schedule.
     *
     * The axis is a floor, never a clip: `hourBoundsFor` widens it for anything falling
     * outside, so the emergency a therapist booked at eight in the evening is still drawn.
     *
     * A program changes a handful of times in a clinic's life, so this is cached for the
     * session rather than refetched alongside the diary.
     */
    const { data: program } = useReactQuery<ITherapistProgram>({
        ...services.therapistProgram.getProgram(user?.id ?? 0, currentTenantId()),
        enabled: !!user?.id,
        staleTime: Infinity,
    });

    const add = useReactMutation(services.appointments.createAppointmentMutation());

    const appointments: IAppointment[] = data ?? [];

    // The clinic's own hours for the days on screen, stretched to hold anything outside them
    // rather than clipping it, and computed across the whole range so every column shares
    // one axis.
    const { firstHour, lastHour } = hourBoundsFor(appointments, program, days);

    const today: string = toDateInputValue(now);
    const nowMinutes: number = now.getHours() * 60 + now.getMinutes();
    const nowOffset: number = ((nowMinutes - firstHour * 60) / 60) * HOUR_HEIGHT;

    /**
     * Only when the line would land on the grid at all.
     *
     * The bounds are not decoration: the grid stretches to fit whatever the range holds, so
     * `firstHour` and `lastHour` differ from one view to the next, and an early morning or
     * a late evening would otherwise put the line above the grid or past its bottom.
     */
    const showsNow: boolean =
        days.includes(today) && nowMinutes >= firstHour * 60 && nowMinutes <= lastHour * 60;

    /**
     * Over and done with.
     *
     * Compared as wall-clock strings, which sort in chronological order, so this is a fact
     * about the appointment rather than about the day being looked at. It used to be gated
     * on today, which meant a session that finished last Tuesday claimed to be still ahead
     * -- fine on a page that only ever showed one day, wrong the moment a week is on screen.
     *
     * Keyed on when it ends, not when it starts, so the session the therapist is in the
     * middle of stays at full strength while the line runs through it. Washing something
     * that is still happening would be a lie.
     */
    const nowStamp: string = toDateTimeInputValue(now);

    const isPast = (appointment: IAppointment): boolean => appointment.endsAt.slice(0, 16) <= nowStamp;

    /**
     * Over, and nobody has said what happened.
     *
     * Held, no-show and cancelled all already answer the question, so only a session left
     * scheduled is still owed one. This is why the past is washed rather than hidden: the
     * wash is the backdrop these have to stand out of.
     */
    const isUnresolved = (appointment: IAppointment): boolean =>
        isPast(appointment) && !appointment.timeBlock && appointment.status === 'SCHEDULED';

    const changeView = (next: ScheduleView): void => {
        setView(next);
        rememberView(next);
    };

    const changeShowCancelled = (next: boolean): void => {
        setShowCancelled(next);
        rememberShowCancelled(next);
    };

    /** A week steps a week; a day steps a day. Both land on a real date via the calendar. */
    const step = (direction: number): void =>
        setAnchor(addDays(anchor, direction * (isWeek ? DAYS_IN_WEEK : 1)));

    const openDay = (day: string): void => {
        setAnchor(day);
        changeView('day');
    };

    /**
     * The day a new entry starts on.
     *
     * In a week there is no one day the therapist is looking at, so today wins when it is
     * on screen and the Monday stands in when it is not. Either way the dialog opens on a
     * date that is actually in view.
     */
    const dayForNewEntry: string = !isWeek ? anchor : days.includes(today) ? today : from;

    const label: string = isWeek
        ? `${formatDayLabel(from, i18n)} – ${formatDayLabel(to, i18n)}`
        : formatDayLabel(anchor, i18n);

    const grid = (
        <ScheduleGrid
            days={days}
            appointmentsByDay={groupByDay(appointments, days)}
            firstHour={firstHour}
            lastHour={lastHour}
            today={today}
            nowOffset={nowOffset}
            showsNow={showsNow}
            isPast={isPast}
            isUnresolved={isUnresolved}
            onSelect={setSelected}
            onCreateAt={(startsAt: string) => {
                setSlotStart(startsAt);
                setIsAdding(true);
            }}
        />
    );

    /** The week grid and the day-name rail are drawn to the same width. */
    const weekTrackWidth: number = LABEL_COLUMN + DAYS_IN_WEEK * WEEK_COLUMN_MIN;

    const addPatient = useReactMutation(services.users.inviteCustomerMutation());

    /**
     * Read back rather than assembled from what the invitation answered with.
     *
     * The picker needs a whole patient, and only the server can say what the row really
     * is: inviting is idempotent, so an address that already belonged to a patient here
     * comes back active rather than pending, and a status invented here would be a guess
     * that the list underneath would immediately contradict.
     */
    const { data: createdPatient, isError: createdPatientFailed } = useReactQuery<IPatient>(
        services.users.getPatient(createdPatientId ?? 0),
        { enabled: createdPatientId !== null }
    );

    useEffect(() => {
        if (createdPatientFailed) {
            showSnackbar(translate(ResourcesIds.ADD_PATIENT_NOT_SELECTABLE), 'warning');
            setCreatedPatientId(null);
        }
    }, [createdPatientFailed, showSnackbar, translate]);

    /**
     * Closes the booking dialog and forgets the patient it was told about.
     *
     * The forgetting is the point: `createdPatient` is a resolved query, so leaving the id
     * behind would have the next booking open with last time's patient already chosen.
     */
    const closeNewEntry = (): void => {
        setIsAdding(false);
        setCreatedPatientId(null);
        setSlotStart(null);
    };

    /**
     * Rejects on failure rather than handling it: the dialog owns the mapping from an API
     * error back onto its own fields, and only a success closes it.
     */
    const submitAddPatient = async (values: AddPatientFormValues): Promise<void> => {
        const created = await addPatient.mutateAsync(toInviteCustomerRequest(values));

        showSnackbar(
            translate(ResourcesIds.ADD_PATIENT_SUCCESS, {
                emailAddress: values.emailAddress.trim(),
            }),
            'success'
        );
        setIsAddPatientOpen(false);
        setCreatedPatientId(created.id);
    };

    /**
     * @param bookedFor the colleague the dialog handed the booking to, when it did. Their
     *                  day is not this one, so the confirmation names them instead of
     *                  claiming an entry that will not appear on the grid below.
     */
    const handleAdd = (request: CreateAppointmentRequest, bookedFor?: string): void => {
        add.mutate(request, {
            onSuccess: () => {
                showSnackbar(bookedFor
                    ? translate(ResourcesIds.MY_CALENDAR_ENTRY_ADDED_FOR, { name: bookedFor })
                    : translate(ResourcesIds.MY_CALENDAR_ENTRY_ADDED), 'success');
                closeNewEntry();
                refetch();
            },
            // The API refuses a clash and says which side it is on, so the message it sends
            // is more use than anything this page could invent.
            // The API refuses a clash and says which side it is on, so its own sentence --
            // "that therapist already has something booked then" -- is more use than anything
            // this page could invent. It used to be discarded for the generic line below,
            // which left somebody staring at a slot the form had offered with no idea why.
            onError: (error: unknown) => showSnackbar(
                splitApiError(error, [], translate,
                    ResourcesIds.MY_CALENDAR_ENTRY_FAILED).formError
                    ?? translate(ResourcesIds.MY_CALENDAR_ENTRY_FAILED),
                'error'
            ),
        });
    };

    return (
        <>
            <Box sx={headerBoxStyles}>
                <Typography variant="h4" component="h1" sx={titleStyles}>
                    {translate(ResourcesIds.MY_CALENDAR_TITLE)}
                </Typography>

                <Box sx={dayControlsStyles}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        sx={viewToggleStyles}
                        value={view}
                        // A group with nothing selected is what a second click on the
                        // active button produces; keep the current view instead.
                        onChange={(_, next: ScheduleView | null) => next && changeView(next)}
                    >
                        <ToggleButton value="day">
                            {translate(ResourcesIds.MY_CALENDAR_VIEW_DAY)}
                        </ToggleButton>
                        <ToggleButton value="week">
                            {translate(ResourcesIds.MY_CALENDAR_VIEW_WEEK)}
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {/* One unit: the arrows have to stay either side of the date they
                        move, and the wrapping row would otherwise split them apart. */}
                    <Box sx={dayPagerStyles}>
                        <Button
                            size="small"
                            sx={dayPagerArrowStyles}
                            aria-label={translate(
                                isWeek
                                    ? ResourcesIds.MY_CALENDAR_PREVIOUS_WEEK
                                    : ResourcesIds.MY_CALENDAR_PREVIOUS
                            )}
                            onClick={() => step(-1)}
                        >
                            <ChevronLeftOutlined />
                        </Button>

                        <Typography sx={dayLabelStyles}>{label}</Typography>

                        <Button
                            size="small"
                            sx={dayPagerArrowStyles}
                            aria-label={translate(
                                isWeek ? ResourcesIds.MY_CALENDAR_NEXT_WEEK : ResourcesIds.MY_CALENDAR_NEXT
                            )}
                            onClick={() => step(1)}
                        >
                            <ChevronRightOutlined />
                        </Button>
                    </Box>

                    <Button size="small" onClick={() => setAnchor(toDateInputValue(new Date()))}>
                        {translate(
                            isWeek ? ResourcesIds.MY_CALENDAR_THIS_WEEK : ResourcesIds.MY_CALENDAR_TODAY
                        )}
                    </Button>

                    {/* The way in that names no hour: it opens on the day in view at the
                        seeded time and takes the free-hour list, so the slot the therapist
                        clicked last time cannot come back with it. */}
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => {
                            setSlotStart(null);
                            setIsAdding(true);
                        }}
                    >
                        {translate(ResourcesIds.MY_CALENDAR_ADD)}
                    </Button>

                    {/* The way back to a booking called off by mistake: its card is the
                        only place that can put it back to scheduled. */}
                    <FormControlLabel
                        sx={showCancelledToggleStyles}
                        label={translate(ResourcesIds.MY_CALENDAR_SHOW_CANCELLED)}
                        control={
                            <Switch
                                size="small"
                                checked={showCancelled}
                                onChange={event => changeShowCancelled(event.target.checked)}
                            />
                        }
                    />
                </Box>
            </Box>

            {/* What this therapist still owes an answer to, whatever week is on screen --
                a request for next month is not in this grid, and each one lapses three
                hours before it starts. Absent when there is nothing to answer. */}
            <PendingRequests />

            {isLoading ? (
                <Box sx={loadingBoxStyles}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {appointments.length === 0 && (
                        <Typography sx={emptyStateStyles}>
                            {translate(
                                isWeek
                                    ? ResourcesIds.MY_CALENDAR_WEEK_EMPTY
                                    : ResourcesIds.MY_CALENDAR_EMPTY
                            )}
                        </Typography>
                    )}

                    {/* The grid is drawn under an empty week too: which columns are empty
                        is exactly the thing a therapist came here to see.

                        Only a week gets the scrolling wrapper. A day is one column and
                        never outgrows the page, so wrapping it would buy nothing and cost
                        a tab stop -- and the region has to be focusable when it does wrap,
                        because a scroll area with nothing focusable inside cannot be
                        reached by keyboard at all. */}
                    {isWeek ? (
                        <>
                            {/* The day names are lifted out of the grid and into a sticky
                                rail of their own, so that scrolling down a long day does
                                not leave seven unlabelled columns on screen. Sideways they
                                still follow the grid, which is what the mirrored ref does
                                -- and the rail is inside this frame rather than loose on
                                the page so that it lets go at the grid's bottom. */}
                            <Box sx={weekFrameStyles}>
                                <Box sx={dayHeaderRailStyles}>
                                    <Box
                                        ref={dayHeaderRailRef}
                                        data-testid="my-calendar-day-header-rail"
                                        sx={scrollTrackStyles(weekTrackWidth)}
                                    >
                                        <DayHeaderRow
                                            days={days}
                                            today={today}
                                            onPickDay={openDay}
                                        />
                                    </Box>
                                </Box>

                                <Box
                                    ref={weekViewportRef}
                                    sx={scrollViewportStyles}
                                    role="region"
                                    tabIndex={0}
                                    aria-label={translate(ResourcesIds.MY_CALENDAR_GRID_LABEL)}
                                >
                                    <Box sx={scrollTrackStyles(weekTrackWidth)}>{grid}</Box>
                                </Box>
                            </Box>

                            {/* Shown only while the grid really is wider than its viewport.
                                Seven columns need 988px, so this survives well past the
                                phone widths the old `sm` cutoff assumed. */}
                            {isWeekScrollable && (
                                <Typography variant="caption" sx={scrollHintStyles}>
                                    {translate(ResourcesIds.MY_CALENDAR_SCROLL_HINT)}
                                </Typography>
                            )}
                        </>
                    ) : (
                        grid
                    )}
                </>
            )}

            <NewEntryDialog
                open={isAdding}
                day={dayForNewEntry}
                startAt={slotStart}
                therapistId={user?.id ?? 0}
                therapistName={memberFullName(user ?? {})}
                isSaving={add.isPending}
                newPatient={createdPatient ?? null}
                onAddPatient={() => setIsAddPatientOpen(true)}
                onClose={closeNewEntry}
                onConfirm={handleAdd}
            />

            <AddPatientDialog
                isOpen={isAddPatientOpen}
                onClose={() => setIsAddPatientOpen(false)}
                onSubmit={submitAddPatient}
            />

            <AppointmentDetailDialog
                appointment={selected}
                onClose={() => setSelected(null)}
                onChanged={refetch}
            />
        </>
    );
};
